// Paddle configuration
// Set these in your environment variables:
// PADDLE_API_KEY - Paddle API key (server-side, never expose to client)
// PADDLE_WEBHOOK_SECRET - Paddle webhook signature secret
// NEXT_PUBLIC_PADDLE_CLIENT_TOKEN - Paddle client-side token
// NEXT_PUBLIC_PADDLE_ENV - "sandbox" or "production"
//
// Price IDs (full-price tier is required; mid/low are optional PPP fallbacks):
// NEXT_PUBLIC_PADDLE_PRICE_ID_PRO_MONTHLY       - Monthly $7 (full)
// NEXT_PUBLIC_PADDLE_PRICE_ID_PRO_ANNUAL        - Annual $48 (full)
// NEXT_PUBLIC_PADDLE_PRICE_ID_PRO_MONTHLY_MID   - Monthly $4 (mid PPP, optional)
// NEXT_PUBLIC_PADDLE_PRICE_ID_PRO_ANNUAL_MID    - Annual $30 (mid PPP, optional)
// NEXT_PUBLIC_PADDLE_PRICE_ID_PRO_MONTHLY_LOW   - Monthly $3 (low PPP, optional)
// NEXT_PUBLIC_PADDLE_PRICE_ID_PRO_ANNUAL_LOW    - Annual $20 (low PPP, optional)

import type { PppTier } from "@/lib/billing/pricing";

export const PADDLE_CONFIG = {
  apiUrl: process.env.NEXT_PUBLIC_PADDLE_ENV === "production"
    ? "https://api.paddle.com"
    : "https://sandbox-api.paddle.com",
  clientToken: process.env.NEXT_PUBLIC_PADDLE_CLIENT_TOKEN || "",
  environment: (process.env.NEXT_PUBLIC_PADDLE_ENV || "sandbox") as "sandbox" | "production",
  prices: {
    full: {
      monthly: process.env.NEXT_PUBLIC_PADDLE_PRICE_ID_PRO_MONTHLY || "",
      annual: process.env.NEXT_PUBLIC_PADDLE_PRICE_ID_PRO_ANNUAL || "",
    },
    mid: {
      monthly: process.env.NEXT_PUBLIC_PADDLE_PRICE_ID_PRO_MONTHLY_MID || "",
      annual: process.env.NEXT_PUBLIC_PADDLE_PRICE_ID_PRO_ANNUAL_MID || "",
    },
    low: {
      monthly: process.env.NEXT_PUBLIC_PADDLE_PRICE_ID_PRO_MONTHLY_LOW || "",
      annual: process.env.NEXT_PUBLIC_PADDLE_PRICE_ID_PRO_ANNUAL_LOW || "",
    },
  },
};

/** Returns the correct Paddle price ID for a given interval and PPP tier.
 *  Falls back to the full-price ID if a PPP-specific one isn't configured. */
export function getPriceId(interval: "monthly" | "annual", pppTier: PppTier): string {
  const tierPrices = PADDLE_CONFIG.prices[pppTier];
  const price = tierPrices[interval];
  if (price) return price;
  // Fall back to full-price tier
  return PADDLE_CONFIG.prices.full[interval];
}

export async function paddleApi(path: string, options?: RequestInit) {
  const res = await fetch(`${PADDLE_CONFIG.apiUrl}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${process.env.PADDLE_API_KEY}`,
      ...options?.headers,
    },
  });
  return res.json();
}

// Verify Paddle webhook signature (HMAC-SHA256)
export async function verifyWebhookSignature(
  rawBody: string,
  signature: string
): Promise<boolean> {
  const secret = process.env.PADDLE_WEBHOOK_SECRET;
  if (!secret) return false;

  try {
    // Parse the ts;h1= format
    const parts = signature.split(";");
    const tsStr = parts.find((p) => p.startsWith("ts="))?.split("=")[1];
    const h1 = parts.find((p) => p.startsWith("h1="))?.split("=")[1];

    if (!tsStr || !h1) return false;

    const signedPayload = `${tsStr}:${rawBody}`;
    const encoder = new TextEncoder();
    const key = await crypto.subtle.importKey(
      "raw",
      encoder.encode(secret),
      { name: "HMAC", hash: "SHA-256" },
      false,
      ["sign"]
    );
    const sig = await crypto.subtle.sign("HMAC", key, encoder.encode(signedPayload));
    const computedBytes = new Uint8Array(sig);

    // Convert h1 hex string to bytes for constant-time comparison
    const h1Bytes = new Uint8Array(h1.length / 2);
    for (let i = 0; i < h1Bytes.length; i++) {
      h1Bytes[i] = parseInt(h1.substring(i * 2, i * 2 + 2), 16);
    }

    if (computedBytes.length !== h1Bytes.length) return false;

    // Constant-time comparison to prevent timing attacks
    let diff = 0;
    for (let i = 0; i < computedBytes.length; i++) {
      diff |= computedBytes[i] ^ h1Bytes[i];
    }
    return diff === 0;
  } catch {
    return false;
  }
}
